import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts } from "../../Styles";
import { EnhancedWidget, CheckBox, CheckStyles, TenDaysGraph } from "..";
import { random } from "lodash";
import { Widget } from "../../Services";
import { Animal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import TenDaysGraphComponents from "../Graphs/TenDaysComponentsViewer/tenDaysGraphComponents";

interface DefaultGraph {
  graphId: number
  header: string
  content: JSX.Element
}

interface GraphWidgetProps {
  widget: Widget;
  animals: Animal[];
}
/**
 * GraphWidget
 * @param header - The title displayed at the top of the widget.
 * @param systemId - The ID of the system to fetch related data.
 * @returns A React component displaying automated reports and progress circles within an enhanced widget.
 */
export default function GraphWidget({ widget, animals }: GraphWidgetProps) {
  const { t } = useTranslation();

  // #region | Dirty data handling
  const [dirtyWidget, setDirtyWidget] = useState(widget);

  useEffect(() => {
    setDirtyWidget(widget);
  }, [widget])

  const resetWidget = () => {
    setDirtyWidget(widget);
  };
  // #endregion

  // #region | Handle choose graph
  const handleCheckBoxClick = (graph: DefaultGraph) => {
    if (dirtyWidget.data.includes(graph.graphId)) {
      setDirtyWidget({
        ...dirtyWidget,
        data: dirtyWidget.data.filter(id => id !== graph.graphId)
      });
    } else {
      setDirtyWidget({
        ...dirtyWidget,
        data: [...dirtyWidget.data, graph.graphId]
      });
    }
  }

  const graphs: DefaultGraph[] = useMemo(() => {

    return [
      {
        graphId: 100,
        header: t("widgets.graph.last10Days", { defaultValue: "Last 10 days feed consumption" }),
        content: <TenDaysGraph data={animals as Animal[]} />
      },
      {
        graphId: 101,
        header: t("widgets.graph.last10DaysComponent", { defaultValue: "Last 10 days feed consumption by component" }),
        content: <TenDaysGraphComponents data={animals as Animal[]} />
      }
    ];
  }, [t, animals]);

  const GraphToShow = useMemo(() => {
    const activeData = widget.data;

    return graphs.find(graph => activeData.includes(graph.graphId))?.content ?? undefined;
  }, [widget.data, graphs]);
  // #endregion

  // #region | Handle choose systems
  // const handleSystems = (system: System) => {
  //   setSystemsToShow(prev =>
  //     prev.includes(system)
  //       ? prev.filter(id => id !== system)
  //       : [...prev, system]              
  //   );
  // };

  // const formatSystemNames = (systems: { name: string }[]) => {
  //   if (systems.length === 0) return "";
  //   if (systems.length === 1) return `${t("from", {defaultValue: "from"})} ${systems[0].name}`;
  //   if (systems.length === 2) return `${t("from", {defaultValue: "from"})} ${systems[0].name} ${t("and", {defaultValue: "and"})} ${systems[1].name}`;
  //   return `${t("from", {defaultValue: "from"})} ${systems.slice(0, -1).map(s => s.name).join(", ")} ${t("and", {defaultValue: "and"})} ${systems[systems.length - 1].name}`;
  // };
  // #endregion

  return (
    <EnhancedWidget
      widget={dirtyWidget}
      oneContent={<div style={{ ...css.rightContent, scrollbarColor: colors.greyTertiary + colors.transparent, }}>
        <div style={{ minWidth: "600px", margin: "0 auto", padding: "10px" }}>
          {GraphToShow !== undefined ? <>{GraphToShow}</> : <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary, textAlign: "center" }}>{t("widgets.graph.noGraphSelected", { defaultValue: "No graph selected" })}</p>}
          {/* {GraphToShow === undefined || widget.placement === 3 ? <></> : <p style={{ ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary, width: "520px", textAlign: "center", marginTop: "10px" }}>{t("widgets.graph.graphIncludesStart", {defaultValue: "The graph includes all animals"})} {formatSystemNames(systemsToShow)}{t("widgets.graph.graphIncludesEnd", {defaultValue: "that has feed consumption data"})}!</p>} */}
        </div>
      </div>}
      overrides={{
        oneContentWidth: "fit-content"
      }}
      changeData={setDirtyWidget}
      onCancel={resetWidget}
      editMode={{
        editHeader: true,
        editLeftContent: (
          <div style={{ height: "250px", display: "flex", flexDirection: "column", gap: "10px", boxSizing: "border-box", width: "100%", overflowY: "auto", scrollbarColor: colors.greyTertiary + colors.transparent, scrollbarWidth: "thin", padding: "20px" }}>
            {graphs.map((graph) => (
              <CheckBox
                key={graph.graphId + random(0, 9999)}
                text={graph.header}
                isChecked={(dirtyWidget.data).includes(graph.graphId)}
                checkStyle={CheckStyles.grey}
                onChange={() => handleCheckBoxClick(graph)}
              />
            ))}
          </div>
        ),
        // editRightContent: widget.placement !== 3 ? 
        //   <div style={{ ...css.rightContentOuter }}>
        //     <div style={{ ...css.rightContentTop }}>
        //       <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary }}>{t("widgets.graph.animalsToInclude", {defaultValue: "Animals to include based on system"})}</p>
        //     </div>
        //     <div style={{ margin: "0px 0px 5px 0px", ...css.rightContentSeperator }}/>
        //     <div style={{ ...css.rightContent }}>
        //       {allSystems.map((system) => (
        //         <CheckBox
        //           key={system.system_id + random(0, 9999)}
        //           text={system.name}
        //           isChecked={systemsToShow.includes(system)}
        //           checkStyle={CheckStyles.greyPrimary}
        //           onChange={() => handleSystems(system)}
        //         />
        //       ))}
        //     </div>
        //   </div>
        // : <></>
      }}
    />
  );
}
